import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { Category } from '@adornis/buchungsheld-db/entities/category.js';
import { Product, ProductFilter } from '@adornis/buchungsheld-db/entities/products.js';
import type { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { WorkshopSet } from '@adornis/buchungsheld-db/entities/WorkshopSet.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { combineLatest, switchMap } from 'rxjs';

const getAllActiveProductsAggregation = (filter: ProductFilter) => {
  const institutionFilter = filter.institutionIDs ? { institutionID: { $in: filter.institutionIDs } } : {};
  const topicFilter = (filter.topics?.length ?? 0) > 0 ? { topic: { $in: filter.topics } } : {};
  const languageFilter = (filter.languages?.length ?? 0) > 0 ? { language: { $in: filter.languages } } : {};
  const presenceFilter = (filter.presences?.length ?? 0) > 0 ? { presenceType: { $in: filter.presences } } : {};
  const categoryFilter = (filter.categoryIDs?.length ?? 0) > 0 ? { categoryID: { $in: filter.categoryIDs } } : {};
  const seatFilter = filter.hasFreeSeats ? { leftCapacity: { $gt: 0 } } : {};

  const timeframeFilter = (filter.timeframeIDs?.length ?? 0) > 0 ? { timeframeID: { $in: filter.timeframeIDs } } : {};

  const descriptionFilter = filter.description
    ? {
        $or: [
          {
            translatedDescriptionHTML: {
              $elemMatch: {
                text: {
                  $regex: filter.description.replace(/ü/g, '&uuml;').replace(/ö/g, '&ouml;').replace(/ä/g, '&auml;'),
                  $options: 'i',
                },
              },
            },
          },
          { name: { $regex: filter.description, $options: 'i' } },
        ],
      }
    : {};

  const productFilterFilter = [
    { $or: [{ isCanceled: false }, { isCanceled: { $exists: false } }] },
    institutionFilter,
    topicFilter,
    languageFilter,
    timeframeFilter,
    presenceFilter,
    descriptionFilter,
    categoryFilter,
    seatFilter,
  ];

  return [{ $match: { $and: productFilterFilter } }, { $match: { isActive: true } }];
};

@Entity()
export class OfferCompactProduct extends AdornisEntity {
  static override _class = 'OfferCompactProduct';

  @Field(type => String)
  _id!: string;

  @Field(type => String)
  categoryID!: string;
}

@Entity()
export class OfferCompactWorkshopSet extends AdornisEntity {
  static override _class = 'OfferCompactWorkshopSet';

  @Field(type => String)
  _id!: string;

  @Field(type => String)
  categoryID!: string;

  @Field(type => [String])
  workshopIDs!: string[];
}

@Entity()
export class InstitutionOffer extends AdornisEntity {
  static override _class = 'InstitutionOffer';

  @Field(type => String)
  institutionID!: string;

  @Field(type => [Category])
  categories!: Category[];

  @Field(type => [OfferCompactProduct])
  products!: OfferCompactProduct[];

  @Field(type => [OfferCompactWorkshopSet])
  workshopSets!: OfferCompactWorkshopSet[];
}

export const getInstitutionOffer = registerQuery({
  operationName: 'getInstitutionOffer',
  type: () => InstitutionOffer,
  params: [{ name: 'filter', type: () => ProductFilter }],
  resolve: (filter?: ProductFilter) => {
    return async (gqlFields: BaseQLSelectionSet<InstitutionOffer>) => {
      const institutionIDs = filter?.institutionIDs;
      if (!institutionIDs) throw new Error('Missing institutionID');
      if (institutionIDs?.length !== 1) throw new Error('Only one institutionID is allowed');
      const [institutionID] = institutionIDs;
      if (!institutionIDs) throw new Error('Missing institutionID');

      // Get products for the institution - converting observable to promise
      const aggregation = getAllActiveProductsAggregation(filter);
      const rawCollection = await getRawCollection<Product>('products-materialized');
      const products = await rawCollection.aggregate<Product>(aggregation).toArray();

      // Group products by categories
      const categoryMap = new Map<string, Category>();
      const workshopSetMap = new Map<string, OfferCompactWorkshopSet>();
      const compactProducts =
        products
          ?.map(product => {
            if (product.category && !categoryMap.has(product.category._id)) {
              categoryMap.set(product.category._id, product.category);
            }

            const affiliation = (product as Workshop).affiliation;
            if (affiliation && affiliation.affiliation === WorkshopSet._class) {
              // TODO: handle case when workshopSet has inactive products
              const workshopSetID = affiliation.affiliationID;
              if (workshopSetMap.has(workshopSetID)) {
                workshopSetMap.get(workshopSetID)!.workshopIDs.push(product._id);
              } else {
                const newSet = new OfferCompactWorkshopSet({
                  _id: workshopSetID,
                  categoryID: product.categoryID || '',
                  workshopIDs: [product._id],
                });

                workshopSetMap.set(workshopSetID, newSet);
              }

              return undefined; // do not add product to compactProducts
            }

            return new OfferCompactProduct({
              _id: product._id,
              categoryID: product.categoryID || '',
            });
          })
          .filter(isDefined) || [];

      // Create the institution offer
      const offer = new InstitutionOffer({
        institutionID,
        categories: Array.from(categoryMap.values()).sort((a, b) => {
          return (a.sortingWeight ?? 99999) - (b.sortingWeight ?? 99999);
        }),
        products: compactProducts,
        workshopSets: Array.from(workshopSetMap.values()),
      });

      return offer;
    };
  },
});

@Entity()
export class ProductFilterOptions extends AdornisEntity {
  static override _class = 'ProductFilterOptions';

  @Field(type => [Category])
  categories?: Category[];

  @Field(type => [Timeframe])
  timeframes?: Timeframe[];

  @Field(type => [String])
  topics?: string[];
}

export const subscribeProductFilterOptions = registerSubscription({
  operationName: 'subscribeInstitutionFilterOptions',
  type: () => ProductFilterOptions,
  params: [{ name: 'filter', type: () => ProductFilter }],
  resolve: filter => {
    return async (gqlFields: BaseQLSelectionSet<ProductFilterOptions>) => {
      return combineLatest([
        watchEntireCollection(Product._collectionName),
        watchEntireCollection(Timeframe._collectionName),
      ]).pipe(
        switchMap(async () => {
          const rawCollection = await getRawCollection<Product>('products-materialized');
          const aggregation = getAllActiveProductsAggregation(filter);
          const result = await rawCollection.aggregate<Product>(aggregation).toArray();
          return result;
        }),

        switchMap(async products => {
          const categoryIDs = new Set(products.map(product => product.categoryID).filter(isDefined));
          const timeframeIDs = new Set(products.map(product => product.timeframeID).filter(isDefined));
          const topics = new Set(products.map(product => product.topic).filter(isDefined));

          const categories = await getByID(Category, Array.from(categoryIDs))(gqlFields.categories ?? { _id: 1 });
          const timeframes = await getByID(Timeframe, Array.from(timeframeIDs))(gqlFields.timeframes ?? { _id: 1 });

          return new ProductFilterOptions({ topics, categories, timeframes });
        }),
      );
    };
  },
});
